/* @flow */

import { Map } from 'immutable'
import CancelablePromise from '../cancelable_promise'

export type DataType = Map<string, mixed>
export type PathEntryType = string | number
export type PathType = Array<PathEntryType>
export type PathDataType = Array<string>

type DataHandlerType = {
  data?: DataType,
  prefix?: PathType,
  parent?: DataHandler,
  parentUpdate?: boolean,
  component?: ?React$Component<*, *>
}

type UpdateFunctionType = (data: Map<string, mixed>) => Map<string, mixed>
type UpdatePromiseFunctionType = (data: Map<string, mixed>) => CancelablePromise

class DataHandler {
  _data: DataType
  _prefix: ?PathType
  _parent: ?DataHandler
  _parentUpdate: boolean
  _component: ?React$Component<any, any>

  constructor({ data, prefix, parent, parentUpdate, component }: DataHandlerType) {
    this._data = data || Map()
    this._prefix = prefix
    this._parent = parent
    this._parentUpdate = parentUpdate || false
    this._component = component
  }

  static _instance: DataHandler
  static instance(data: Map<string, mixed>): DataHandler {
    return (this._instance = this._instance || new DataHandler({ data }))
  }

  component(component: ?React$Component<any, any>) {
    this._component = component
  }

  /**
   * update data with given current data
   * @param  {Function} fn function which returns new data and gets data
   * @return {CancelablePromise}
   * @example
   * update((data) => data.setIn(['test'], 'hallo'))
   */
  update(fn: UpdateFunctionType, update?: boolean = true): CancelablePromise {
    return this._updateData(fn(this.getData()), update)
  }

  /**
   * update data with a async function which returns a promise
   * @param  {Function} fn function which returns a promise and gets data
   * @return {CancelablePromise}
   * @example
   * updatePromise((data) => Promise.resolve(data.setIn(['test'], 'hallo')))
   */
  updatePromise = (fn: UpdatePromiseFunctionType, update?: boolean = true): CancelablePromise => {
    return fn(this.getData()).then((data) => this._updateData(data, update))
  }

  get = (path: string | number): mixed => {
    return this.getIn([path])
  }

  getIn = (path: PathType): mixed => {
    const data = this.getData()
    // $FlowFixMe
    return data.hasIn(path) ? data.getIn(path) : null
  }

  set = (path: string, value: mixed, update?: boolean = true): CancelablePromise => {
    return this.setIn([path], value, update)
  }

  setIn = (path: PathType, newData: mixed, update?: boolean = true): CancelablePromise => {
    // $FlowFixMe
    return this.update((data) => data.setIn(path, newData), update)
  }

  prefix = (prefix: PathType, update: boolean = false): DataHandler => {
    const newPrefix: PathType = ((this._prefix || []).concat(prefix): any)
    return new DataHandler({
      prefix: newPrefix,
      parent: this._parent || this,
      parentUpdate: update
    })
  }

  copy = (component?: ?React$Component<*, *>): DataHandler => {
    const handler = new DataHandler({ data: this.getData(), component })
    return handler
  }

  replace = (dataHandler: DataHandler, update: boolean = true): CancelablePromise => {
    return this._updateData(dataHandler.getData(), update)
  }

  getData = (): DataType => {
    if (this._parent && Array.isArray(this._prefix)) {
      // $FlowFixMe
      return this._parent.getData().getIn(this._prefix)
    } else {
      return this._data
    }
  }

  // private functions

  _updateData = (newData: DataType, update: boolean = false): CancelablePromise => {
    if (this._parent != null && Array.isArray(this._prefix)) {
      const data = this._parent.getData()
      const path: PathType = this._prefix || []
      return (
        this._parent
          // $FlowFixMe
          ._updateData(data.setIn(path, newData), this._parentUpdate)
          .then(update ? this._updateComponentState : () => {})
      )
    } else {
      this._data = newData
      return update ? this._updateComponentState() : CancelablePromise.resolve()
    }
  }

  _updateComponentState = (): CancelablePromise => {
    return new CancelablePromise((resolve) => {
      if (this._component != null) {
        this._component.setState({}, () => resolve(this))
      } else {
        resolve(this)
      }
    })
  }
}

export default DataHandler
